import { isEmail, isEmpty } from 'validator';
import { ConfigService } from '../services/configService';
import { ApplicationDto } from '../types/ApplicationDto';
import { ConfigDto } from '../types/ConfigDto';
import { OrganizationDto } from '../types/OrganizationDto';
import { getElement } from '../utils/getElement';
import { htmlIds } from '../constants';
import { getConfig } from '../config';
import { LoginToAGWResponse } from '../types/common';
import { Logger } from '../utils/logger';

interface FormData {
  email: string;
  password: string;
}

interface ValidationError {
  field: keyof FormData;
  message: string;
}

class LoginFormError extends Error {
  constructor(public readonly errors: ValidationError[]) {
    super('Validation failed');
    this.name = 'LoginFormError';
  }
}

const STAGING_QUICK_LOGIN_GOOGLE_SHEET_URL =
  'https://docs.google.com/spreadsheets/d/1RGc2556oofoHaygeylH5GWTm9vTtmup8dbgZKAceGUg';

export class LoginForm {
  private readonly configService: ConfigService;
  private readonly staging: boolean;
  private readonly local: boolean;

  constructor() {
    this.configService = ConfigService.getInstance();
    const instanceType = getConfig().INSTANCE_TYPE;
    this.staging = instanceType === 'staging';
    this.local = instanceType === 'local';
  }

  private static readonly INPUT_IDS = {
    email: 'email',
    password: 'password', // eslint-disable-line sonarjs/no-hardcoded-passwords
  } as const;

  private static readonly ERROR_MESSAGES = {
    EMAIL_REQUIRED: 'Email is required',
    EMAIL_INVALID: 'Email must have valid format',
    PASSWORD_REQUIRED: 'Password is required', // eslint-disable-line sonarjs/no-hardcoded-passwords
  } as const;

  // Highlight class for the selected organization/application tile (styled in style.css).
  private static readonly ACTIVE_TILE_CLASS = 'ula-tile--active';

  // Per-organization accent, used to tint the selected tile's glow/ring in that brand's color.
  private static readonly ORG_ACCENTS: Record<string, string> = {
    TIO: '#97c93f',
    TF: '#1e9fe0',
    RTSG: '#e8701a',
    DBG: '#a6d417',
  };

  // Applications are internal modules (monochrome icons), so their selected glow stays neutral white.
  private static readonly APP_ACCENT = '#eef3e6';

  private organizationAccent(code: string): string {
    return LoginForm.ORG_ACCENTS[code.toUpperCase()] ?? '#b8e82c';
  }

  public get formInputIds(): typeof LoginForm.INPUT_IDS {
    return LoginForm.INPUT_IDS;
  }

  private getFormInputErrorElement(fieldName: keyof FormData): HTMLDivElement {
    return getElement<HTMLDivElement>(`#${fieldName}-error`);
  }

  private validateFormData(formData: FormData): void {
    const errors: ValidationError[] = [];

    if (isEmpty(formData.email)) {
      errors.push({
        field: 'email',
        message: LoginForm.ERROR_MESSAGES.EMAIL_REQUIRED,
      });
    } else if (!isEmail(formData.email)) {
      errors.push({
        field: 'email',
        message: LoginForm.ERROR_MESSAGES.EMAIL_INVALID,
      });
    }

    if (isEmpty(formData.password)) {
      errors.push({
        field: 'password',
        message: LoginForm.ERROR_MESSAGES.PASSWORD_REQUIRED,
      });
    }

    // Always refresh the display so fields that became valid clear their stale error.
    this.displayValidationErrors(errors);

    if (errors.length > 0) {
      throw new LoginFormError(errors);
    }
  }

  private clearFieldError(field: keyof FormData): void {
    const errorElement = this.getFormInputErrorElement(field);
    errorElement.innerHTML = '&nbsp;';
    errorElement.classList.add('opacity-0');
    errorElement.classList.remove('opacity-100');
  }

  private displayValidationErrors(errors: ValidationError[]): void {
    // Clear all errors first
    for (const field of Object.keys(LoginForm.INPUT_IDS)) {
      this.clearFieldError(field as keyof FormData);
    }

    // Display new errors
    for (const error of errors) {
      const errorElement = this.getFormInputErrorElement(error.field);
      errorElement.innerHTML = error.message;
      errorElement.classList.add('opacity-100');
      errorElement.classList.remove('opacity-0');
    }
  }

  /** Clears a field's validation error as soon as the user starts editing it. */
  private setupFieldErrorClearing(): void {
    for (const field of Object.keys(LoginForm.INPUT_IDS)) {
      const input = getElement<HTMLInputElement>(`#${field}`);
      input.addEventListener('input', () => this.clearFieldError(field as keyof FormData));
    }
  }

  private getApplicationHtml(applications: ApplicationDto[]): string {
    const activeOrganizationCode = this.configService.getOrganizationCode();
    const activeApplication = this.configService.getActiveApplication();

    return applications
      .filter((app) => app.allowedTenants.includes(activeOrganizationCode))
      .map((application) => {
        const isActive = application.name === activeApplication;
        return this.renderApplicationItem(application, isActive);
      })
      .join('');
  }

  private renderApplicationItem(application: ApplicationDto, isActive: boolean): string {
    const activeClass = isActive ? ` ${LoginForm.ACTIVE_TILE_CLASS}` : '';
    const iconUrl = `/apps/${application.name.toLowerCase()}.svg`;
    return `
      <div
        class="ula-tile group flex w-[5.5rem] flex-col justify-center items-center gap-2 text-center rounded-xl border border-white/10 bg-white/[0.03] px-3 py-3.5 cursor-pointer transition duration-150 ease-out hover:-translate-y-0.5 hover:bg-white/[0.06] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand${activeClass}"
        id="application-${application.name}"
        data-testid="ula-application-${application.name.toLowerCase()}"
        style="--tile-glow:${LoginForm.APP_ACCENT}"
        role="button"
        tabindex="0"
        aria-pressed="${isActive}"
      >
        <span
          class="ula-appicon h-9 w-9"
          role="img"
          aria-label="${application.name} icon"
          style="-webkit-mask:url(${iconUrl}) center/contain no-repeat;mask:url(${iconUrl}) center/contain no-repeat"
        ></span>
        <span class="font-semibold text-xs tracking-wide">${application.name}</span>
      </div>
    `;
  }

  private getOrganizationHtml(organizations: OrganizationDto[]): string {
    const activeOrganizationCode = this.configService.getOrganizationCode();

    return organizations
      .map((organization) => {
        const isActive = organization.code === activeOrganizationCode;
        return this.renderOrganizationItem(organization, isActive);
      })
      .join('');
  }

  private renderOrganizationItem(organization: OrganizationDto, isActive: boolean): string {
    const activeClass = isActive ? ` ${LoginForm.ACTIVE_TILE_CLASS}` : '';
    const accent = this.organizationAccent(organization.code);
    return `
      <div
        class="ula-tile flex flex-col justify-center items-center gap-2 text-center rounded-xl border border-white/10 bg-white/[0.03] px-2 py-3 cursor-pointer transition duration-150 ease-out hover:-translate-y-0.5 hover:bg-white/[0.06] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand${activeClass}"
        id="organization-${organization.code}"
        data-testid="ula-organization-${organization.code.toLowerCase()}"
        style="--tile-glow:${accent}"
        role="button"
        tabindex="0"
        aria-pressed="${isActive}"
      >
        <span class="flex h-12 w-12 items-center justify-center rounded-xl bg-white/[0.04] p-2 ring-1 ring-inset ring-white/5">
          <img
            src="/tenants/${organization.code.toLowerCase()}.png"
            alt="${organization.name} logo"
            class="max-h-full max-w-full"
          />
        </span>
        <span class="font-semibold text-[11px] tracking-wide">${organization.code}</span>
      </div>
    `;
  }

  /** Makes a role="button" tile respond to Enter/Space like a click (keyboard access). */
  private addTileKeyboardActivation(element: HTMLDivElement): void {
    element.addEventListener?.('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        element.click();
      }
    });
  }

  private async renderApps(): Promise<void> {
    const apps = await this.configService.getApps();
    const container = getElement<HTMLDivElement>(`#${htmlIds.applicationsContainer}`);

    container.innerHTML = this.getApplicationHtml(apps);

    for (const node of container.childNodes) {
      const element = node as HTMLDivElement;
      element.addEventListener('click', () => {
        const applicationName = element.id.split('-')[1];
        this.setActiveApplication(applicationName);
      });
      this.addTileKeyboardActivation(element);
    }
  }

  private setActiveApplication(application: string): void {
    const container = getElement<HTMLDivElement>(`#${htmlIds.applicationsContainer}`);

    for (const node of container.childNodes) {
      (node as HTMLDivElement)?.classList?.remove(LoginForm.ACTIVE_TILE_CLASS);
      (node as HTMLDivElement)?.setAttribute?.('aria-pressed', 'false');
    }

    const activeTile = getElement<HTMLDivElement>(`#application-${application}`);
    activeTile.classList.add(LoginForm.ACTIVE_TILE_CLASS);
    activeTile.setAttribute('aria-pressed', 'true');
    this.configService.setActiveApplication(application);

    const url = new URL(globalThis.location.toString());
    url.searchParams.set('application', application);
    globalThis.history.pushState({}, '', url.toString());
  }

  private setActiveOrganization(organizationCode: string): void {
    const container = getElement<HTMLDivElement>(`#${htmlIds.organizationsContainer}`);

    for (const node of container.childNodes) {
      (node as HTMLDivElement)?.classList?.remove(LoginForm.ACTIVE_TILE_CLASS);
      (node as HTMLDivElement)?.setAttribute?.('aria-pressed', 'false');
    }

    const activeTile = getElement<HTMLDivElement>(`#organization-${organizationCode}`);
    activeTile.classList.add(LoginForm.ACTIVE_TILE_CLASS);
    activeTile.setAttribute('aria-pressed', 'true');
    this.configService.setOrganizationCode(organizationCode);

    const url = new URL(globalThis.location.toString());
    url.searchParams.set('organization', organizationCode);
    globalThis.history.pushState({}, '', url.toString());

    this.renderApps().catch((error) => {
      Logger.logError('Failed to render applications:', error);
    });
  }

  private setupOrganizationListeners(): void {
    const container = getElement<HTMLDivElement>(`#${htmlIds.organizationsContainer}`);

    for (const node of container.childNodes) {
      const element = node as HTMLDivElement;
      element.addEventListener('click', () => {
        const organizationCode = element.id.split('-')[1];
        this.setActiveOrganization(organizationCode);
      });
      this.addTileKeyboardActivation(element);
    }
  }

  /**
   * Split-screen shell: brand panel on the left, the given controls column on the right.
   * Shared by the logged-out and logged-in views so both keep one identity.
   */
  private renderShell(rightColumn: string): string {
    return `
      ${this.renderStagingBanner()}
      <div class="grid min-h-screen grid-cols-1 lg:grid-cols-[1.05fr_1fr]">
        ${this.renderBrandPanel()}
        <main class="flex items-center justify-center px-6 py-12 sm:px-10 lg:border-l lg:border-white/10 lg:px-14">
          <div class="w-full max-w-md">
            ${rightColumn}
          </div>
        </main>
      </div>
    `;
  }

  private stagingBannerMarkup(): string {
    const icon =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5" aria-hidden="true"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>';
    return `<div id="${htmlIds.stagingBanner}" class="fixed top-0 left-0 z-50 flex w-full items-center justify-center gap-2.5 border-b border-red-500/40 bg-red-950/80 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-red-300 backdrop-blur-md">${icon}Toto není produkční instance</div>`;
  }

  private renderStagingBanner(): string {
    return this.staging ? this.stagingBannerMarkup() : '';
  }

  /** Whether the app runs against the local environment (enables the debug controls). */
  public get isLocal(): boolean {
    return this.local;
  }

  /** Exposes the staging banner markup so the local debug toggle can inject it. */
  public getStagingBannerMarkup(): string {
    return this.stagingBannerMarkup();
  }

  /** Local-only debug controls: small icon buttons pinned to the bottom-right of the screen. */
  private renderDebugPanel(): string {
    if (!this.local) {
      return '';
    }
    const btnClass =
      'flex h-7 w-7 items-center justify-center rounded-md border border-white/10 bg-black/40 text-slate-400 backdrop-blur-sm transition hover:bg-white/10 hover:text-slate-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand';
    const alertIcon =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5" aria-hidden="true"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>';
    const bellIcon =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>';
    return `
      <div class="fixed bottom-3 right-3 z-40 flex gap-1.5">
        <button type="button" id="${htmlIds.debugToggleBannerBtn}" title="Toggle staging banner" aria-label="Toggle staging banner" class="${btnClass}">${alertIcon}</button>
        <button type="button" id="${htmlIds.debugErrorToastBtn}" title="Test error toast" aria-label="Test error toast" class="${btnClass}">${bellIcon}</button>
      </div>
    `;
  }

  private renderBrandPanel(): string {
    return `
      <aside class="ula-brand relative flex flex-col overflow-hidden px-8 py-10 lg:min-h-screen lg:px-14 lg:py-14">
        <div class="ula-pulse absolute -top-24 right-[-8rem] h-[440px] w-[440px] lg:h-[520px] lg:w-[520px]" aria-hidden="true">
          <span class="ula-ring"></span><span class="ula-ring"></span><span class="ula-ring"></span><span class="ula-ring"></span>
          <span class="ula-core"></span>
        </div>
        <div class="relative z-10">
          <img src="/tenants/dbg.png" alt="Digital Boost Group" class="h-14 w-14 lg:h-16 lg:w-16" />
        </div>
        <div class="relative z-10 mt-14 lg:mt-[4.5rem]">
          <p class="font-mono text-[11px] font-semibold uppercase tracking-[0.32em] text-brand">Unified login</p>
          <h1 class="mt-5 max-w-md font-display text-[2rem] font-semibold leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-[3.25rem]">
            Your key to the whole <span class="text-brand">AI-native</span> boost stack.
          </h1>
          <p class="mt-6 max-w-sm text-sm leading-relaxed text-slate-300">
            One identity across HUB, ERP and ADMIN — every module under a single AI layer that reads your data, answers in plain language and automates the busywork.
          </p>
          <dl class="mt-9 hidden max-w-md space-y-3.5 lg:block">
            <div class="flex items-baseline gap-4">
              <dt class="w-14 shrink-0 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-brand">HUB</dt>
              <dd class="text-[13px] leading-relaxed text-slate-400">Culture, feedback, goals, absence and The Game.</dd>
            </div>
            <div class="flex items-baseline gap-4">
              <dt class="w-14 shrink-0 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-brand">ERP</dt>
              <dd class="text-[13px] leading-relaxed text-slate-400">Projects, finance, capacity planning and profit.</dd>
            </div>
            <div class="flex items-baseline gap-4">
              <dt class="w-14 shrink-0 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-brand">ADMIN</dt>
              <dd class="text-[13px] leading-relaxed text-slate-400">Org structure, permissions and data sync.</dd>
            </div>
          </dl>
          <div class="mt-9 hidden max-w-md border-t border-white/10 pt-6 lg:block">
            <p class="font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-brand">AI layer over your data</p>
            <p class="mt-3 max-w-sm text-[13px] leading-relaxed text-slate-300">
              Not a bolt-on chatbot. One layer reads across every module, understands context and acts.
            </p>
            <ul class="mt-4 grid grid-cols-1 gap-x-6 gap-y-2.5 text-[13px] text-slate-400 sm:grid-cols-2">
              <li class="flex items-start gap-2.5"><span class="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-brand"></span>Ask in plain language, answered with the source</li>
              <li class="flex items-start gap-2.5"><span class="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-brand"></span>Flags at-risk projects &amp; unusual costs early</li>
              <li class="flex items-start gap-2.5"><span class="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-brand"></span>Reads documents and books them automatically</li>
              <li class="flex items-start gap-2.5"><span class="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-brand"></span>Suggests staffing &amp; drafts sales follow-ups</li>
            </ul>
          </div>
        </div>
      </aside>
    `;
  }

  private eyebrow(index: string, label: string): string {
    return `
      <div class="mb-3 flex items-center gap-3">
        <span class="font-mono text-[11px] font-semibold tracking-[0.2em] text-brand">${index}</span>
        <span class="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">${label}</span>
        <span class="h-px flex-1 bg-white/10"></span>
      </div>
    `;
  }

  private renderLoginForm(configs: ConfigDto[]): string {
    const organizations = this.getOrganizationHtml(configs.map((config) => config.organization));

    const rightColumn = `
      <div>
        ${this.eyebrow('01', 'Organization')}
        <div class="grid grid-cols-4 gap-2" id="${htmlIds.organizationsContainer}">
          ${organizations}
        </div>
      </div>
      <div class="mt-7">
        ${this.eyebrow('02', 'Application')}
        <div class="flex flex-wrap gap-2" id="${htmlIds.applicationsContainer}"></div>
      </div>
      <div class="mt-8">
        ${this.eyebrow('03', 'Sign in')}
        <button
          id="${htmlIds.googleLogin}"
          class="flex w-full items-center justify-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm font-medium text-slate-100 transition hover:bg-white/[0.06] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        ><img class="h-5" src="/google-logo.svg" alt="" />Sign in with google</button>
        <div class="my-4 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
          <span class="h-px flex-1 bg-white/10"></span>or<span class="h-px flex-1 bg-white/10"></span>
        </div>
        <form class="w-full" id="${htmlIds.loginForm}" data-testid="ula-login-form" novalidate>
          ${this.renderFormField('email', 'Email', 'email')}
          ${this.renderFormField('password', 'Password', 'password')}
          <div class="mt-5">
            <button
              type="submit"
              data-testid="ula-submit-login-btn"
              class="ula-cta w-full rounded-xl bg-brand px-4 py-3 text-sm font-semibold text-[#0a0e12] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >Sign in</button>
          </div>
        </form>
        ${this.renderStagingQuickLoginLink()}
        ${this.renderLocalQuickLoginButton()}
        ${this.renderDebugPanel()}
      </div>
    `;

    return this.renderShell(rightColumn);
  }

  private renderStagingQuickLoginLink(): string {
    if (!this.staging) {
      return '';
    }
    return `
      <div class="mt-5 border-t border-white/10 pt-4 text-center">
        <a
          href="${STAGING_QUICK_LOGIN_GOOGLE_SHEET_URL}"
          target="_blank"
          rel="noopener noreferrer"
          class="text-sm text-brand underline underline-offset-2 hover:brightness-110 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >Quick login (Google Sheet)</a>
      </div>
    `;
  }

  private renderLocalQuickLoginButton(): string {
    if (!this.local) {
      return '';
    }
    const quickLoginClass =
      'quick-login-button w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2 text-left text-sm text-slate-200 transition hover:bg-white/[0.06] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand';
    return `
      <div class="mt-5 border-t border-white/10 pt-4">
        <div class="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Local quick login</div>
        <div class="flex flex-col gap-2">
          <button
            data-email="vaclav.miculka@techfides.cz"
            data-password="123456"
            class="${quickLoginClass}"
          >vaclav.miculka@techfides.cz</button>
          <button
            data-email="stefan.malcek@techfides.cz"
            data-password="123456"
            class="${quickLoginClass}"
          >stefan.malcek@techfides.cz</button>
        </div>
      </div>
    `;
  }

  private renderFormField(id: keyof FormData, label: string, type: string): string {
    return `
      <div class="${id === 'password' ? 'mt-3' : ''}">
        <label
          for="${id}"
          class="mb-1.5 block text-sm font-medium text-slate-300"
        >${label}</label>
        <input
          id="${id}"
          data-testid="ula-${id}"
          name="${id}"
          type="${type}"
          autocomplete="${type === 'password' ? 'current-password' : id}"
          class="block w-full rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 transition focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/40"
        />
        <div id="${id}-error" class="transition-all text-xs text-red-400 overflow-hidden opacity-0 mt-1 leading-tight">&nbsp;</div>
      </div>
    `;
  }

  private renderLoggedInContent(configs: ConfigDto[], user: LoginToAGWResponse): string {
    const organizations = this.getOrganizationHtml(configs.map((config) => config.organization));
    const displayName = user.displayName ?? user.email ?? '';
    const photoHTML = user.photoURL
      ? `<img src="${user.photoURL}" alt="User photo" class="h-10 w-10 rounded-full ring-1 ring-white/15" />`
      : '';
    const impersonationBanner = user.isImpersonated
      ? `<div class="fixed top-0 left-0 z-50 w-full bg-orange-500 py-2 text-center font-bold text-white" data-testid="ula-impersonation-banner">⚠️ Application is running in impersonation mode ⚠️</div>`
      : '';

    const rightColumn = `
      <div>
        ${this.eyebrow('01', 'Organization')}
        <div class="grid grid-cols-4 gap-2" id="${htmlIds.organizationsContainer}">
          ${organizations}
        </div>
      </div>
      <div class="mt-7">
        ${this.eyebrow('02', 'Application')}
        <div class="flex flex-wrap gap-2" id="${htmlIds.applicationsContainer}"></div>
      </div>
      <div class="mt-8">
        ${this.eyebrow('03', 'Session')}
        <div class="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
          ${photoHTML}
          <span class="text-sm text-slate-400">Signed in as <strong id="${htmlIds.userDisplayName}" data-testid="ula-user-display-name" class="cursor-pointer select-none font-semibold text-slate-100" title="Double-click to impersonate">${displayName}</strong></span>
        </div>
        <dialog
          id="${htmlIds.impersonateDialog}"
          data-testid="ula-impersonate-dialog"
          class="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0e1309] p-6 text-slate-100 shadow-2xl backdrop:bg-black/60 backdrop:backdrop-blur-sm"
        >
          <h2 class="mb-4 font-display text-base font-semibold">Impersonate collaborator</h2>
          <input
            id="${htmlIds.impersonateInput}"
            data-testid="ula-impersonate-input"
            type="text"
            placeholder="Collaborator UUID"
            class="mb-4 block w-full rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 transition focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/40"
          />
          <div class="flex justify-end gap-2">
            <button
              id="${htmlIds.impersonateCancelBtn}"
              data-testid="ula-impersonate-cancel-btn"
              class="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-slate-200 transition hover:bg-white/[0.06] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >Cancel</button>
            <button
              id="${htmlIds.impersonateConfirmBtn}"
              data-testid="ula-impersonate-confirm-btn"
              class="ula-cta rounded-xl bg-brand px-4 py-2 text-sm font-semibold text-[#0a0e12] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >Confirm</button>
          </div>
        </dialog>
        <div class="mt-5 flex flex-wrap gap-3">
          <button
            id="${htmlIds.goToAppBtn}"
            data-testid="ula-go-to-app-btn"
            class="ula-cta rounded-xl bg-brand px-5 py-2.5 text-sm font-semibold text-[#0a0e12] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >Open app</button>
          <button
            id="${htmlIds.logoutBtn}"
            data-testid="ula-logout-btn"
            class="rounded-xl border border-white/10 bg-white/[0.03] px-5 py-2.5 text-sm font-semibold text-slate-200 transition hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >Sign out</button>
          ${
            user.isImpersonated
              ? `<button
            id="${htmlIds.stopImpersonationBtn}"
            data-testid="ula-stop-impersonation-btn"
            class="rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
          >Stop impersonation</button>`
              : ''
          }
        </div>
      </div>
      ${this.renderDebugPanel()}
    `;

    return `${impersonationBanner}${this.renderShell(rightColumn)}`;
  }

  public async renderLoggedIn(container: HTMLDivElement, user: LoginToAGWResponse): Promise<void> {
    const configs = await this.configService.getFirebaseConfigs();
    container.innerHTML = this.renderLoggedInContent(configs, user);
    this.setupOrganizationListeners();
    await this.renderApps();
  }

  public async render(container: HTMLDivElement): Promise<void> {
    const configs = await this.configService.getFirebaseConfigs();
    container.innerHTML = this.renderLoginForm(configs);
    this.setupOrganizationListeners();
    this.setupFieldErrorClearing();
    await this.renderApps();
  }

  public validateForm(formData: FormData): void {
    this.validateFormData(formData);
  }
}
